/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { extend, map } from 'underscore';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { makeStyles, Input, shorthands } from '@fluentui/react-components';
import { SearchFilled } from '@fluentui/react-icons';
import { InvestImageDataService } from '@bingads-webui-campaign/invest-image-data-service';
import { GridSectionName } from '../../model/constants';
import { SectionTilesGrid, TilesGridLoadingCell } from '../ads-studio-tile-grid';
import { AdsStudioSpinner } from '../ads-studio-spinner';

const PAGE_SIZE = 20;

const useStyles = makeStyles({
  contentWrapper: {
    paddingTop: '8px',
  },
  searchArea: {
    marginBottom: '16px',
    display: 'flex',
    flexWrap: 'wrap',
  },
  input: {
    width: '260px',
    marginRight: '8px',
    marginBottom: '8px',
  },
  listbox: {
    minWidth: 'auto',
  },
  helpLink: {
    color: '#3073ad',
    ...shorthands.textDecoration('underline'),
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
  videoNameWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: 5,
    marginBottom: 5,
  },
  videoName: {
    position: 'relative',
    left: 0,
    bottom: 0,
    width: 'auto',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  videoBtnWrapper: {
    '& svg': {
      visibility: 'hidden !important',
    },
  },
  video: {
    opacity: '1 !important',
  },
});

const emptyButtonStyle = {
  padding: '2px',
  margin: '0px',
  background: 'none',
  color: 'unset',
  cursor: 'pointer',
  borderImage: 'unset',
  borderColor: 'unset',
  borderStyle: 'none',
};

const ASSET_TYPE_VIDEOS = 2;

export const InvestVideoPanelContent = (props) => {
  const {
    i18n, aid, cid, setSelectedVideos, logger,
  } = props;
  const classes = useStyles();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [videos, setVideos] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const selectedVideos = useRef([]);
  const searchQuery = useRef('');
  const skip = useRef(0);
  const hasMore = useRef(true);

  const onInputChange = useCallback((ev, data) => {
    const { value = '' } = data || {};
    searchQuery.current = value;
  }, []);
  const investImageService = useMemo(() => new InvestImageDataService({ cid, aid }), [aid, cid]);

  const getInvestVideos = useCallback(options => investImageService.getInvestAssets(options), [investImageService]);

  const setLoadVideosError = (error, creativeId) => {
    let errorMessage = `Could not load invest video assets ${creativeId ? `for creativeId: ${creativeId}` : ''}`;
    if (error && error.responseJSON && error.responseJSON.value && error.responseJSON.value.length > 0) {
      const errorCode = error.responseJSON.value[0].Code;
      const message = error.responseJSON.value[0].Message || errorMessage;
      errorMessage = `${errorCode} ${message}`;
    }
    notificationApi.showWarning(i18n.getString(_TL_('{{errorMessage}}.'), { errorMessage }));
  };

  /* istanbul ignore next */
  const loadVideos = useCallback(({ creativeId = searchQuery.current, $skip = skip.current, $query = {} }) => {
    if ($skip === 0) {
      selectedVideos.current = [];
      setSelectedVideos(selectedVideos.current);
      setIsLoadingFirstPage(true);
    } else {
      setIsLoadingMore(true);
    }

    const queryParams = {
      appName: 'CampaignUI',
      assetTypeId: ASSET_TYPE_VIDEOS,
      $skip,
      $top: PAGE_SIZE,
    };

    if (creativeId) {
      queryParams.creativeId = creativeId;
    }

    getInvestVideos({
      customQueries: extend(queryParams, $query),
    }).then((data) => {
      hasMore.current = data['@odata.count'] > data.value.length + $skip;
      setTotalCount(data['@odata.count'] || 0);
      setVideos(prevVideos => [
        ...($skip > 0 ? prevVideos : []),
        ...map(data.value, (item) => {
          const video = {
            url: item.Url,
            id: item.XandrAssetId,
            name: item.Description,
            description: item.Description,
          };
          return video;
        }),
      ]);

      if ($skip === 0) {
        setIsLoadingFirstPage(false);
      } else {
        setIsLoadingMore(false);
      }
    }).catch((error) => {
      setLoadVideosError(error, creativeId);
      if ($skip === 0) {
        setIsLoadingFirstPage(false);
      } else {
        setIsLoadingMore(false);
      }
    });
  }, [getInvestVideos, setSelectedVideos]);

  useEffect(() => {
    loadVideos({ $skip: 0 });
  }, []);

  useEffect(() => {
    logger.logTrace('Ads Studio - Navigating to view: InvestVideo');
    return () => {
      logger.logTrace('Ads Studio - Leaving view: InvestVideo');
    };
  }, [logger]);

  /* istanbul ignore next */
  const loadNextPage = useCallback(() => {
    if (hasMore.current) {
      const newSkip = skip.current + PAGE_SIZE;
      skip.current = newSkip;
      loadVideos({ $skip: newSkip });
    }
  }, [loadVideos]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      skip.current = 0;
      loadVideos({
        $skip: 0,
      });
    }
  }, [loadVideos]);

  /* istanbul ignore next */
  const onSelectVideo = useCallback((videoId) => {
    const originSelectedLength = selectedVideos.current.length;
    const newSelectedList = selectedVideos.current.filter(({ id }) => id !== videoId);
    if (newSelectedList.length === originSelectedLength) {
      const selectedItem = videos.find(({ id }) => id === videoId);
      selectedVideos.current.push(selectedItem);
    } else {
      selectedVideos.current = newSelectedList;
    }
    setSelectedVideos([...selectedVideos.current]);
  }, [videos, setSelectedVideos]);

  const videosGridSection = useMemo(() => ({
    sectionName: GridSectionName.Asset,
    sectionClassName: 'ads-studio-invest-video-section',
    itemKey: 'id',
    items: videos,
    totalItemCount: totalCount,
    config: {
      scrollContainer: '[role="main"], .app-content-container-col',
      minItemHeight: 42,
    },
  }), [videos, totalCount]);

  const sectionGridData = useMemo(
    () => ([videosGridSection]),
    [videosGridSection]
  );

  const onRenderLoadingCell = useCallback(({ items, totalItemCount }) => {
    if ((items.length < totalItemCount) && (items.length > 0)) {
      return (
        <TilesGridLoadingCell onLoading={loadNextPage} key={items.length} >
          {isLoadingMore && (
          <div className="section-loading">
            <AdsStudioSpinner useCustomSize />
          </div>
            )}
        </TilesGridLoadingCell>
      );
    }
    return null;
  }, [isLoadingMore, loadNextPage]);

  const onRenderCell = useCallback(({ item }) => {
    const { id, name } = item;

    return (
      <div>
        <button
          onClick={() => onSelectVideo(id)}
          id={item.id}
          style={emptyButtonStyle}
          className={classes.videoBtnWrapper}
        >
          <video autoPlay={false} loop className={classes.video} height="100%" width="100%">
            <source src={item.url} />
          </video>
        </button>
        <div className={classes.videoNameWrapper}>
          <span className={classes.videoName} title={name}>
            {name}
          </span>
        </div>
      </div>
    );
  }, [classes.videoName, classes.videoNameWrapper, onSelectVideo]);

  return (
    <div className={classNames('invest-video-content-container', classes.contentWrapper)}>
      <div className={classes.searchArea}>
        <Input
          className={classNames('invest-video-search-input', classes.input)}
          onChange={onInputChange}
          placeholder={i18n.getString(_TL_('Search by Invest Creative ID'))}
          contentBefore={<SearchFilled />}
          onKeyDown={onKeyDown}
        />
      </div>
      { isLoadingFirstPage && !videos.length && <AdsStudioSpinner useCustomSize customStyle={{ marginTop: '50px' }} /> }
      {!!videos.length && (
        <SectionTilesGrid
          sectionData={sectionGridData}
          onRenderCell={onRenderCell}
          onRenderLoadingCell={onRenderLoadingCell}
          loadNextPage={loadNextPage}
        />
      )}
    </div>
  );
};

InvestVideoPanelContent.prototype = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  setSelectedVideos: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};
